import React from "react";
import { useTranslation } from "react-i18next";

/**
 * TrustBadge — badge de confiance réutilisable (Phase 13.c).
 *
 * Table statique en miroir de backend/services/trust_badges.py::BADGE_TABLE.
 * Utilisé par :
 *   - le bloc « Sources utilisées » du chat Blue,
 *   - l'écran de revue Assisted Publishing (flags editorial).
 *
 * Props :
 *   - type   : un des 5 types Phase 11, ou undefined -> badge neutre
 *   - badge  : objet badge déjà résolu côté serveur ({type,label,tone})
 *   - review : true -> rend le flag court de revue éditoriale ([À vérifier]…)
 *   - size   : "sm" (défaut) | "xs"
 */
export const BADGE_TABLE = {
  VERIFIED_FACT: { label: "Fait vérifié", tone: "positive", reviewFlag: "[Fait vérifié]" },
  PARTNER_PROVIDED_INFO: { label: "Information partenaire", tone: "neutral", reviewFlag: "[Information partenaire]" },
  PARTNER_CLAIM: { label: "Déclaration partenaire", tone: "attention", reviewFlag: "[À vérifier]" },
  PROMOTIONAL_CLAIM: { label: "Contenu promotionnel", tone: "warning", reviewFlag: "[Claim promotionnel]" },
  EDITORIAL_ITERCATUS_OBSERVATION: { label: "Observation ITERCATUS", tone: "editorial", reviewFlag: "[Observation ITERCATUS]" },
};

const UNKNOWN = { label: "Source", tone: "neutral", reviewFlag: "[À classer]" };

const TONE_CLASSES = {
  positive: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-slate-50 text-slate-600 border-slate-200",
  attention: "bg-amber-50 text-amber-700 border-amber-200",
  warning: "bg-orange-50 text-orange-700 border-orange-200",
  editorial: "bg-sky-50 text-sky-700 border-sky-200",
};

export function TrustBadge({ type, badge, review = false, size = "sm", className = "" }) {
  const { t } = useTranslation();
  const resolvedType = (badge?.type || type || "").toUpperCase();
  const entry = BADGE_TABLE[resolvedType] || UNKNOWN;
  const localized = t(`trustBadge.${resolvedType}`, { defaultValue: badge?.label || entry.label });
  const label = review ? entry.reviewFlag : localized;
  const tone = badge?.tone || entry.tone;
  const toneClass = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
  const sizeClass = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium leading-none ${toneClass} ${sizeClass} ${className}`}
      title={`${t("trustBadge.confidence", { defaultValue: "Niveau de confiance" })} : ${localized}`}
      aria-label={`${t("trustBadge.confidence", { defaultValue: "Niveau de confiance" })} : ${localized}`}
      data-testid={`trust-badge-${resolvedType.toLowerCase() || "unknown"}`}
    >
      {label}
    </span>
  );
}

export default TrustBadge;
